import type { IconGroupId } from '../../constants/icon-groups'

import { COMMON } from './common'

const RAIL = 'Leiste'

/** A pane is put away and brought back by name — "Notizen einklappen". */
const collapsePane = (pane: string) => `${pane} einklappen`
const expandPane = (pane: string) => `${pane} ausklappen`

// Text a reusable control owns and says identically wherever it is mounted —
// a table with no rows, a picker's search box. Text a control receives as a
// prop belongs to the caller, not here.
export const CONTROLS = {
  searchPlaceholder: 'Suchen …',
  /** The pills a row has no width left for, as one more pill. */
  morePills: (n: number) => `+${n}`,
  /** What a search turned up nothing says, wherever the search box sits. */
  noResults: 'Keine Ergebnisse gefunden',
  dataTable: {
    empty: 'Keine Einträge gefunden',
    /** Names the kebab menu at the end of a row. */
    rowActions: 'Aktionen',
    noResultsDescription: 'Andere Suchbegriffe oder Filter versuchen.',
    loadMore: 'Mehr laden …',
    allLoaded: 'Alle Einträge geladen',
  },
  /**
   * How many rows a list shows, over any surface that has a filter bar — a
   * table, a tree, a tile grid. Named after the bar: a table, a tree and a
   * tile grid all say it.
   */
  toolbar: {
    /** Default noun for the rows, where a screen names no other. */
    entriesUnit: 'Einträge',
    // `unit` is the caller's noun for its rows — "Objekte", "Einträge".
    count: (shown: number, unit: string) => `${shown} ${unit}`,
    countOfTotal: (shown: number, total: number, unit: string) =>
      `${shown} von ${total} ${unit}`,
    /** Infinite-scroll lists say how much of the stream is on screen. */
    loadedCount: (shown: number, unit: string) => `${shown} ${unit} geladen`,
  },
  select: {
    placeholder: 'Auswählen …',
    // The list stops rendering past a cap, so the count shown is not the count
    // matched — saying both is what tells the user to keep typing.
    truncated: (shown: number, total: number) =>
      `Zeige erste ${shown} von ${total} — bitte Suche verfeinern`,
  },
  tagInput: {
    placeholder: 'Tag hinzufügen …',
  },
  /** A date typed the German way or picked from the calendar beside it. */
  dateInput: {
    placeholder: 'TT.MM.JJJJ',
    openCalendar: 'Kalender öffnen',
    previousMonth: 'Vorheriger Monat',
    nextMonth: 'Nächster Monat',
    previousYear: 'Vorheriges Jahr',
    nextYear: 'Nächstes Jahr',
    invalid: 'Kein gültiges Datum',
    /** Monday first, as a German calendar reads. */
    weekdays: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    clear: 'Leeren',
  },
  filter: {
    all: 'Alle',
    /** Clears every filter the toolbar holds. */
    reset: 'Filter zurücksetzen',
  },
  /**
   * The bar of one pill per filter. Every string here is about filtering
   * itself — the nouns it filters by come from the caller's own fields.
   */
  filterBar: {
    /** Over the two ends of a range. */
    minMax: 'Von / bis',
    /** A range with one open end, in a pill. */
    upTo: (value: string) => `${COMMON.to} ${value}`,
    fromValue: (value: string) => `${COMMON.from} ${value}`,
    span: (min: string, max: string) => `${min} – ${max}`,
    /** The foot of the sheet names what pressing it will show. */
    showCount: (n: number, unit: string) => `${n} ${unit} anzeigen`,
    noHits: 'Kein Treffer',
    /** Under the empty list, where the narrowing is what emptied it. */
    emptyHint: 'Keiner der Einträge passt auf alle gesetzten Filter zusammen.',
  },
  /**
   * A filter kept under a name. Verbs only, no noun: what a screen calls one —
   * a Sammlung, an Ansicht — comes in as the caller's own word.
   */
  views: {
    none: 'Noch keine gespeichert',
    save: 'Aktuellen Filter speichern',
    saveAsNew: 'Als neue speichern',
    update: 'Aktualisieren',
    unsaved: COMMON.unsavedChanges,
    deleteMessage:
      'Der gespeicherte Filter wird entfernt, die Einträge bleiben.',
  },
  /** The divider between a list and its detail pane. */
  resizePanes: 'Breite der Spalten ändern',
  /** The buttons on that divider, named after what the pane holds —
   *  "Notizen einklappen". */
  collapsePane,
  expandPane,
  // The tile grid an entity's pictures are managed in.
  imageGallery: {
    captionPlaceholder: 'Titel…',
    removeImage: 'Bild entfernen',
    removeTitle: 'Bild entfernen?',
    /** The row on the pane shows what fits; the rest is behind this. */
    manage: 'Bilder verwalten',
    manageCount: (count: number) =>
      `${count} ${COMMON.imageUnit(count)} · ziehen zum Sortieren`,
  },

  /** The toolbar over a markdown field, and the two things it has to ask for. */
  markdownEditor: {
    bold: 'Fett',
    italic: 'Kursiv',
    heading: (level: number) => `Überschrift ${level}`,
    bulletList: 'Aufzählung',
    orderedList: 'Nummerierte Liste',
    quote: 'Zitat',
    link: 'Link',
    linkPrompt: 'Leer lassen, um den Link zu entfernen.',
    image: 'Bild aus der Mediathek',
    imageWidth: 'Breite',
    imageWidthOf: (percent: number) =>
      `Bild auf ${percent} % der Spaltenbreite`,
    /** Whether the text runs beside a narrowed picture, and on which side. */
    wrap: {
      none: 'Eigene Zeile',
      left: 'Bild links, Text rechts daneben',
      right: 'Bild rechts, Text links daneben',
    },
    video: 'Video einbetten',
    videoPrompt: 'Adresse des Videos bei YouTube oder Vimeo.',
    videoUnsupported: 'Eingebettet werden YouTube und Vimeo.',
    undo: 'Rückgängig',
    redo: 'Wiederherstellen',
  },

  iconPicker: {
    /** Heads the dialog the entry's own mark opens. */
    title: 'Icon wählen',
    searchPlaceholder: 'Icon suchen …',
    noResults: 'Keine Icons gefunden',
    /** The trigger, while nothing is picked. */
    empty: 'Kein Icon',
    /** What is drawn while nothing is picked, where the caller names nothing
     *  more specific. Stated beside the button that clears a pick. */
    emptyFallback: 'Ohne Icon wird ein Standard-Zeichen angezeigt.',
    /** Clears the pick, in the popover's foot and on the trigger. */
    clear: 'Kein Icon',
    all: 'Alle',
    /** First entry of the rail: what the entry's own title suggests. */
    suggestions: 'Vorschläge',
    /** Names of the groups the grid is split into and the chips filter by. */
    groups: {
      building: 'Gebäude',
      rooms: 'Räume & Möbel',
      sanitary: 'Bad & Sanitär',
      kitchen: 'Küche & Essen',
      structure: 'Struktur',
      electric: 'Elektro & Licht',
      climate: 'Heizung & Klima',
      tools: 'Werkzeug & Bau',
      devices: 'Geräte & Technik',
      outdoor: 'Außen & Verkehr',
      documents: 'Dokumente & Geld',
      misc: 'Sonstiges',
    } satisfies Record<IconGroupId, string>,
  },
  /** The switch among the global controls. Named after what pressing it does,
   *  not after what is on screen. */
  theme: {
    toDark: 'Dunkles Design',
    toLight: 'Helles Design',
  },
  /** The rail down the left, wide or narrow. */
  nav: {
    /** The side nav's own name, for the pair of verbs below. */
    rail: RAIL,
    collapse: collapsePane(RAIL),
    expand: expandPane(RAIL),
  },
  unitSelect: {
    /** Label of the "no unit" option inside the list. */
    empty: 'keine',
    /** Prompt on the trigger while nothing is picked. */
    placeholder: 'Einheit …',
    search: 'Einheit suchen …',
  },
  pdf: {
    pending: 'Wird erstellt …',
    error: 'Das PDF konnte nicht erstellt werden.',
  },
  copyField: {
    failed:
      'Kopieren ging nicht. Bitte den Text markieren und von Hand kopieren.',
  },
  cronJob: {
    trigger: 'Jetzt ausführen',
    processed: 'Verarbeitet',
    running: 'Wird ausgeführt …',
    idle: 'Bereit',
    lastRun: 'Letzter Lauf',
    nextRun: 'Nächster Lauf',
    lastError: 'Letzter Lauf fehlgeschlagen',
    triggeredBy: 'Gestartet von',
    resultTitle: 'Letztes Ergebnis',
  },
} as const
